import React, { useState, useEffect } from 'react';
import { deviceOptionLabel } from '../../utils/deviceOptionLabel';
import { hasPendingReturn } from '../../utils/devicePickerFilter';
import { simOptionLabel } from '../../utils/simOptionLabel';
import { rentalHoldFor, withRentalHoldLabel } from '../../utils/rentalHold';
import type { RentalHoldInfo } from '../../types/rentalHold';
import { useNavigate } from 'react-router-dom';
import { showAlert } from '../../utils/alert';
import Layout from '../../components/ui/Layout';
import SearchableSelect from '../../components/ui/SearchableSelect';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { RiAddLine, RiDeleteBinLine, RiSaveLine, RiArrowLeftLine, RiErrorWarningLine, RiForbid2Line, RiAlertLine, RiInformationLine, RiAddCircleLine, RiWalletLine, RiListCheck, RiCalculatorLine, RiGiftLine, RiCalendarLine, RiRemoteControlLine, RiCheckLine } from 'react-icons/ri';
import companyService, { CompanyDepositSettings, CompanyTotalDepositInfo } from '../../services/companyService';
import siteService from '../../services/siteService';
import productService from '../../services/productService';
import deviceService from '../../services/deviceService';
import deviceTypeService from '../../services/deviceTypeService';
import simService from '../../services/simService';
import { excludeSimsPendingReturn, isSimBoundToDevice } from '../../utils/simAvailability';
import PrepaidUntilFields from '../../components/rental/PrepaidUntilFields';
import rentalService from '../../services/rentalService';
import ValidatedInput from '../../components/ui/ValidatedInput';
import codeGeneratorService from '../../services/codeGeneratorService';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { usePermission } from '../../hooks/usePermission';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { cn } from '../../utils/cn';
import { computeWaivedSavings, countWaivedItems } from '../../utils/depositCalc';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { countSerializedDevices, MULTI_SERIALIZED_WARNING } from '../../utils/serializedDeviceCount';

// ประเภทข้อมูลสำหรับฟอร์ม
interface RentalFormData {
  rental_number: string;
  site_id: number | '';
  company_id: number | '';
  // BUG-U1 (2026-09-08): เอา product_id "ระดับสัญญา" ออก — ย้ายไปเป็นของแต่ละรายการ
  // (RentalItemFormData.product_id) เหมือน RentalEditPageV2 (GAP-29). เดิมเลือกสินค้าครั้งเดียว
  // ที่ header แล้ว prefill ค่าเช่า/มัดจำทับ "ทุกแถว" → เพิ่ม 3 รายการ เลือกสินค้าครั้งเดียว
  // ได้ค่าเช่า 400 ทั้ง 3 แถว ยอดรวมโชว์ 1,200 ทั้งที่ควรเป็น 400 · ตอน submit ก็ยัด product_id
  // ตัวเดียวกันลงทุกแถว ทำให้ "1 สัญญา = อุปกรณ์หลายชิ้น คนละสินค้า/คนละราคา" (VMS Plus) ทำไม่ได้
  rental_date: string;
  billing_start_date: string; // วันที่เริ่มเก็บเงิน
  prepaid_until: string;      // ชำระล่วงหน้าถึงวันที่ (ว่าง = ไม่ได้ชำระล่วงหน้า)
  prepaid_note: string;
  expected_return_date: string;
  rental_period_days: number;
  notes: string;
  rental_items: RentalItemFormData[];
  created_by?: number | null;
  // New fields for data migration
  delivery_date: string; // วันที่ส่งมอบอุปกรณ์
}

interface RentalItemFormData {
  id?: string; // สำหรับ tracking ใน frontend
  product_id: number | ''; // BUG-U1 / GAP-29: สินค้าเป็นของ "รายการ" ไม่ใช่ของสัญญา (แต่ละแถวตั้งราคาเองได้)
  device_type_id: number | '';
  device_id: number | '';
  sim_id: number | ''; // เพิ่ม sim_id
  quantity: number;
  rental_rate_per_month: number; // ค่าเช่าต่อเดือน
  deposit_amount: number;
  deposit_waived: boolean; // ยกเว้นค่ามัดจำ
  notes: string;
  is_software_only: boolean; // เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
  // Remote Access (AnyDesk) — ต่อเครื่อง
  anydesk_id: string;
  remote_access_notes: string;
  // Frontend-only tracking fields for change detection
  _prev_device_type_id?: number | '';
  _prev_device_id?: number | '';
  _prev_deposit_amount?: number;
}

// ประเภทข้อมูลสำหรับ dropdown options
interface DropdownOption {
  id: number;
  name: string;
  code?: string;
}

interface Company {
  id: number;
  name: string;
  code: string;
}

interface Site {
  id: number;
  name: string;
  code: string;
  company_id: number;
}

interface Product {
  id: number;
  name: string;
  code: string;
  monthly_rental_rate?: number;
  monthlyRentalRate?: number;
  deposit_amount?: number;
}

interface Device {
  id: number;
  // R4U-2: ธง "ยังมีใบตามเก็บค้าง" จาก /devices/available (ดู types/device.ts) — picker ต้องไม่เสนอ
  has_pending_return?: boolean | null;
  pending_return_count?: number | null;
  // fix ค (20 ก.ย. 2026): "พร้อมใช้งานแต่ยังถูกล็อก" ด้วยสัญญาอื่นที่ยังไม่จบ — ต้องเลือกได้อยู่
  // แต่ติดป้ายเตือนก่อนเลือก (ดู utils/rentalHold.ts) ไม่ใช่ซ่อนทิ้งแบบ has_pending_return
  rental_hold?: RentalHoldInfo | null;
  serial_number: string;
  code?: string;
  device_type_id?: number;
  device_type?: {
    id: number;
    name: string;
    code: string;
  };
  device_model_id?: number;
  device_model?: { id: number; name: string };
  model?: string;
  status: string;
  // Inventory fields (matches /types/device.ts canonical Device)
  is_serialized?: boolean;
  quantity?: number;
  unit?: string;
}

interface Sim {
  id: number;
  phone_number: string;
  serial_number?: string;
  operator: string;
  sim_type: string;
  status: string;
  // fix ค (20 ก.ย. 2026) — ดูคอมเมนต์เดียวกันที่ interface Device
  rental_hold?: RentalHoldInfo | null;
}

const RentalCreatePageV2: React.FC = () => {
  const { getUserId, hasValidUser } = useCurrentUser();
  const { canCreate, hasPermission } = usePermission();
  // BUG-S2005-002: "บันทึก + เริ่มเช่าเลย" = อนุมัติทันที → backend บังคับ rentals.approve (403) — ซ่อนปุ่มถ้าไม่มีสิทธิ์
  const canStartNow = hasPermission('rentals.approve');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // BUG-S2003-001 (KD3 S2003 NEG-RENT-001, 2026-09-02): handleSubmit await checkCodeExists ก่อน setLoading(true)
  // → double-click ยิง POST /rentals/ 2 ครั้ง (ครั้งที่สอง 400 เลขที่ซ้ำ + error toast ตามหลัง "สำเร็จ") · lock แบบ synchronous
  // ตั้งแต่ต้น (pattern เดียวกับ SupplierPage/S001) reset ทุก exit path
  const isSubmittingRef = React.useRef(false);
  // BUG-178 FIX: per-field loading indicators for device dropdowns
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [loadingSims, setLoadingSims] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isFormDirty, setIsFormDirty] = useState(false);

  // Unsaved changes warning
  const { showModal, onConfirmLeave, onCancelLeave } = useUnsavedChanges(isFormDirty);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const handleBack = () => {
    if (isFormDirty) {
      setShowBackConfirm(true);
    } else {
      navigate('/rentals/list');
    }
  };

  // Permission check
  useEffect(() => {
    if (!canCreate('rentals')) {
      navigate('/rentals/list');
    }
  }, [canCreate, navigate]);

  // ข้อมูลฟอร์ม
  const [formData, setFormData] = useState<RentalFormData>(() => {
    // สร้างวันที่ปัจจุบันในรูปแบบ YYYY-MM-DD โดยใช้ local timezone
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const todayString = `${year}-${month}-${day}`;

    return {
      rental_number: '',
      site_id: '',
      company_id: '',
      rental_date: todayString, // วันที่ปัจจุบัน
      billing_start_date: '', // ไม่บังคับ — เว้นว่าง (backend fallback ใช้วันที่เช่า) [PO 2026-06-24]
      prepaid_until: '',
      prepaid_note: '',
      expected_return_date: '', // ไม่บังคับ — เว้นว่าง = สัญญาเปิด (ไม่ auto-fill +1ปี แล้ว) [PO 2026-06-24]
      rental_period_days: 0, // คำนวณเมื่อมีวันคืน
      notes: '',
      rental_items: [{
        id: Date.now().toString(),
        product_id: '', // BUG-U1: สินค้าเลือกต่อรายการ
        device_type_id: '',
        device_id: '',
        sim_id: '', // เพิ่ม sim_id
        quantity: 1,
        rental_rate_per_month: 0,
        deposit_amount: 0,
        deposit_waived: false, // ยกเว้นค่ามัดจำ
        notes: '',
        is_software_only: false, // เช่า Software อย่างเดียว
        // Remote Access (AnyDesk)
        anydesk_id: '',
        remote_access_notes: ''
      }],
      created_by: null, // จะถูกตั้งค่าใน useEffect
      // New fields for data migration
      delivery_date: '' // วันที่ส่งมอบอุปกรณ์
    };
  });

  // ข้อมูลสำหรับ dropdown
  const [companies, setCompanies] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [filteredSites, setFilteredSites] = useState<Site[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<any[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [availableDevices, setAvailableDevices] = useState<Device[]>([]);
  const [sims, setSims] = useState<Sim[]>([]);
  const [availableSims, setAvailableSims] = useState<Sim[]>([]);

  // ข้อมูลค่ามัดจำรวมของบริษัท
  const [companyDepositInfo, setCompanyDepositInfo] = useState<CompanyTotalDepositInfo | null>(null);

  // State สำหรับข้อความแจ้งเตือนรหัสซ้ำ
  const [codeError, setCodeError] = useState<string>('');
  // R62: immediate date-feedback (parity with ContractCreatePage's dateError) —
  // surfaced when expected_return_date < rental_date, before the user hits save.
  const [dateError, setDateError] = useState<string>('');

  // การตั้งค่าค่ามัดจำของบริษัท - ใช้ข้อมูลจาก company data แทน hardcode
  const depositSettings = React.useMemo<CompanyDepositSettings>(() => {
    if (companyDepositInfo) {
      return {
        deposit_amount: companyDepositInfo.deposit_limit
      };
    }
    // fallback เมื่อไม่มีข้อมูลบริษัท
    return {
      deposit_amount: 40000
    };
  }, [companyDepositInfo]);

  // อัพเดท created_by เมื่อ user โหลดเสร็จ
  useEffect(() => {
    if (hasValidUser) {
      const userId = getUserId();
      setFormData(prev => ({
        ...prev,
        created_by: userId
      }));
    }
  }, [hasValidUser, getUserId]);

  // โหลดข้อมูลเริ่มต้น
  useEffect(() => {
    loadInitialData();
    generateRentalNumber();
  }, []);

  // [PO 2026-06-24] เอา auto-fill "วันครบสัญญา = วันที่เช่า + 1 ปี" ออก —
  // ทั้งวันครบสัญญา + วันเริ่มเก็บเงิน เป็น "ไม่บังคับ" และเริ่มต้น "เว้นว่าง" ให้ support
  // สร้าง skeleton ก่อน แล้วเซลเติมทีหลัง (ห้าม revert กลับเป็น auto-fill/บังคับ)

  // สร้างเลขที่การเช่าอัตโนมัติ
  const generateRentalNumber = async () => {
    try {
      const nextCodePreview = await codeGeneratorService.getNextCodePreview('rental', true);
      setFormData(prev => ({
        ...prev,
        rental_number: nextCodePreview.next_code
      }));
    } catch (error) {
      console.error('Error generating rental number:', error);
    }
  };

  // กรองไซต์ตามบริษัทที่เลือก และโหลดข้อมูล
  useEffect(() => {
    if (formData.company_id) {
      const filtered = sites.filter(site => site.company_id === formData.company_id);
      setFilteredSites(filtered);
      
      // โหลดข้อมูลค่ามัดจำรวม (ฟีเจอร์หลัก)
      loadCompanyTotalDeposit(formData.company_id);
    } else {
      setFilteredSites([]);
      setCompanyDepositInfo(null);
    }
  }, [formData.company_id, sites]);

  // คำนวณระยะเวลาเมื่อมีการเปลี่ยนแปลงวันที่
  useEffect(() => {
    if (formData.rental_date && formData.expected_return_date) {
      const startDate = new Date(formData.rental_date);
      const endDate = new Date(formData.expected_return_date);
      
      // ตรวจสอบว่าวันที่ถูกต้อง
      if (startDate <= endDate) {
        setDateError('');
        const diffTime = endDate.getTime() - startDate.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 เพื่อรวมวันแรกด้วย

        setFormData(prev => ({
          ...prev,
          rental_period_days: diffDays,
          rental_items: prev.rental_items.map(item => ({
            ...item,
            rental_period_days: diffDays
          }))
        }));
      } else {
        // R62: ถ้าวันที่คืนน้อยกว่าวันที่เช่า — แจ้งทันที (ไม่ต้องรอ save) + clamp period=1
        setDateError('วันที่คืนต้องไม่น้อยกว่าวันที่เช่า');
        setFormData(prev => ({
          ...prev,
          rental_period_days: 1,
          rental_items: prev.rental_items.map(item => ({
            ...item,
            rental_period_days: 1
          }))
        }));
      }
    } else {
      setDateError('');  // ไม่ได้ระบุวันคืน (สัญญาเปิด) → ไม่มี error
    }
  }, [formData.rental_date, formData.expected_return_date]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // โหลดข้อมูลบริษัท
      try {
        const companiesResponse = await companyService.getCompanies({ page: 1, limit: 1000 });
        setCompanies((companiesResponse.items || []) as any);
      } catch (error) {
        console.error('Error loading companies:', error);
        setCompanies([]);
        showAlert('แจ้งเตือน', 'ไม่สามารถโหลดข้อมูลบริษัทได้', 'warning');
      }

      // โหลดข้อมูลไซต์ที่ active เท่านั้น
      try {
        const sitesResponse = await siteService.getSites({ page: 1, limit: 1000, status: 'active' });
        setSites(sitesResponse.items || []);
      } catch (error) {
        console.error('Error loading sites:', error);
        setSites([]);
        showAlert('แจ้งเตือน', 'ไม่สามารถโหลดข้อมูลโครงการได้', 'warning');
      }

      // โหลดข้อมูลสินค้าที่ active เท่านั้น
      try {
        const productsResponse = await productService.getProducts({ page: 1, limit: 100, is_active: true });
        setProducts(productsResponse.items || []);
      } catch (error) {
        console.error('Error loading products:', error);
        setProducts([]);
        showAlert('แจ้งเตือน', 'ไม่สามารถโหลดข้อมูลสินค้าได้', 'warning');
      }

      // โหลดข้อมูลประเภทอุปกรณ์
      // BUG-178 FIX: per-field loading indicator
      setLoadingDeviceTypes(true);
      try {
        const deviceTypesResponse = await deviceTypeService.getDeviceTypes({ page: 1, limit: 100 });

        if (deviceTypesResponse && deviceTypesResponse.items && Array.isArray(deviceTypesResponse.items)) {
          setDeviceTypes(deviceTypesResponse.items);
        } else {
          console.warn('Invalid device types response structure:', deviceTypesResponse);
          setDeviceTypes([]);
          showAlert('ไม่สามารถโหลดข้อมูลประเภทอุปกรณ์ได้', 'ข้อมูลที่ได้รับจากเซิร์ฟเวอร์ไม่ถูกต้อง', 'warning');
        }
      } catch (error) {
        console.error('Error loading device types:', error);
        setDeviceTypes([]);
        showAlert('ไม่สามารถโหลดข้อมูลประเภทอุปกรณ์ได้', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อดึงข้อมูลประเภทอุปกรณ์ กรุณาตรวจสอบการเชื่อมต่อและลองใหม่อีกครั้ง', 'warning');
      } finally {
        setLoadingDeviceTypes(false);
      }

      // โหลดข้อมูลอุปกรณ์ที่พร้อมใช้งานเท่านั้น
      // BUG-178 FIX: per-field loading indicator
      setLoadingDevices(true);
      try {
        const availableDevicesResponse = await deviceService.getAvailableDevices();

        if (availableDevicesResponse && Array.isArray(availableDevicesResponse)) {

          setDevices(availableDevicesResponse as any);
          setAvailableDevices(availableDevicesResponse as any);
        } else {
          console.warn('❌ Invalid available devices response structure:', availableDevicesResponse);
          setDevices([]);
          setAvailableDevices([]);
          showAlert('ไม่สามารถโหลดข้อมูลอุปกรณ์ได้', 'ข้อมูลอุปกรณ์ที่ได้รับจากเซิร์ฟเวอร์ไม่ถูกต้อง', 'warning');
        }
      } catch (error) {
        console.error('❌ Error loading devices:', error);
        setDevices([]);
        setAvailableDevices([]);
        showAlert('ไม่สามารถโหลดข้อมูลอุปกรณ์ได้', 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์เพื่อดึงข้อมูลอุปกรณ์ กรุณาตรวจสอบการเชื่อมต่อและลองใหม่อีกครั้ง', 'warning');
      } finally {
        setLoadingDevices(false);
      }

      // โหลดข้อมูลซิมการ์ด
      // BUG-178 FIX: per-field loading indicator
      setLoadingSims(true);
      try {
        // BUG-S592-AVAILCAP (จุดเดียวกันคนละที่): กรอง AVAILABLE ฝั่ง client แต่โหลดมาแค่ 100
        // จากซิมทั้งหมด 691 → ซิมว่างส่วนใหญ่ไม่โผล่ใน picker · RentalEditPageV2 ใช้ 1000 อยู่แล้ว
        const simsResponse = await simService.getAllSims();

        if (simsResponse && simsResponse.items && Array.isArray(simsResponse.items)) {
          const allSims = simsResponse.items;

          // SIM state machine: AVAILABLE ⇄ IN_USE; รองรับ ACTIVE/Thai label เพื่อ legacy data
          const AVAILABLE_STATUSES = new Set([
            'AVAILABLE', 'available', 'ACTIVE', 'active', 'พร้อมใช้งาน',
          ]);
          // Lane D (L2 ฝั่งซิม): ตัดซิมที่ยังมีใบตามเก็บค้างออกด้วย — สถานะอย่างเดียวไม่พอ
          // (prod: AVAILABLE 16 เบอร์ที่ใบยังเปิด → เลือกได้แต่กดบันทึกโดน 400 เงียบ ๆ)
          // ฟอร์มสร้างใหม่ไม่มีซิมเดิมของตัวเอง จึงไม่ต้องมี keepIds
          const availableSims = excludeSimsPendingReturn(
            allSims.filter((sim: any) =>
              AVAILABLE_STATUSES.has(sim.status) && !isSimBoundToDevice(sim)
            )
          );

          setSims(allSims);
          setAvailableSims(availableSims);
        } else {
          console.warn('Invalid sims response structure:', simsResponse);
          setSims([]);
          setAvailableSims([]);
        }
      } catch (error) {
        console.error('Error loading sims:', error);
        setSims([]);
        setAvailableSims([]);
        showAlert('แจ้งเตือน', 'ไม่สามารถโหลดข้อมูลซิมการ์ดได้', 'warning');
      } finally {
        setLoadingSims(false);
      }

    } catch (error) {
      console.error('Error loading initial data:', error);
      showAlert('เกิดข้อผิดพลาด', 'เกิดข้อผิดพลาดร้ายแรงในการโหลดข้อมูล', 'error');
    } finally {
      setLoading(false);
    }
  };

  // โหลดข้อมูลค่ามัดจำรวมของบริษัท
  const loadCompanyTotalDeposit = async (companyId: number) => {
    try {
      const data = await companyService.getTotalDeposit(companyId);
      setCompanyDepositInfo(data);
    } catch (error) {
      console.error('Error loading company total deposit:', error);
      setCompanyDepositInfo(null);
    }
  };

  // จัดการการเปลี่ยนแปลงข้อมูลรายการ
  const updateRentalItem = (index: number, field: keyof RentalItemFormData, value: any) => {
    const updatedItems = [...formData.rental_items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };

    // Clear error for this field
    const errorKey = `item_${index}_${field === 'device_type_id' ? 'device_type' : field === 'device_id' ? 'device' : field === 'rental_rate_per_month' ? 'rate' : field === 'deposit_amount' ? 'deposit' : field}`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }

    // คำนวณค่ามัดจำอัตโนมัติ - ให้ผู้ใช้กรอกเอง เพราะไม่มีอัตราตายตัว
    if (field === 'rental_rate_per_month' || field === 'quantity') {
      // ไม่คำนวณอัตโนมัติ เพื่อให้ผู้ใช้มีความยืดหยุ่นในการกำหนดค่ามัดจำ
      // เพียงแค่คำนวณมูลค่ารวมเพื่อแสดงผล
    }

    // กรองอุปกรณ์ตาม device_type ที่เลือก
    if (field === 'device_type_id' && value) {

      // ถ้าอุปกรณ์ที่เลือกไว้ไม่ตรงกับ device_type ใหม่ ให้ clear
      const deviceTypeDevices = devices.filter(device =>
        device.device_type_id === value && (device.status === 'AVAILABLE' || device.status === 'available' || device.status === 'พร้อมใช้งาน')
      );

      if (updatedItems[index].device_id &&
          !deviceTypeDevices.find(d => d.id === updatedItems[index].device_id)) {
        updatedItems[index].device_id = '';
      }
    }

    setFormData(prev => ({ ...prev, rental_items: updatedItems }));
  };

  // PO (ค่ามัดจำต่อเครื่อง): ดึงค่ามัดจำ default ของสินค้าที่เลือกไว้ในรายการนั้น
  // - API products คืน camelCase และเป็น "string" (เช่น depositAmount: "3000.00"); เผื่อ snake_case/number ไว้ด้วย
  // - coerce เป็นตัวเลขเสมอ (เดิม typeof===number ทำให้ string ตกเป็น 0) · ถ้าไม่มี/ไม่ได้เลือก → 0
  // BUG-U1: รับ product ของ "แถวนั้น" เสมอ (เดิม default fallback ไป formData.product_id ระดับสัญญา)
  const getProductDeposit = (productId: number | ''): number => {
    if (!productId) return 0;
    const product = products.find(p => p.id === Number(productId));
    const dep = product?.deposit_amount ?? (product as any)?.depositAmount;
    const n = Number(dep);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  };

  // BUG-U1: เลือกสินค้า "ต่อรายการ" — prefill ค่าเช่า/มัดจำเฉพาะแถวนั้นแถวเดียว
  // - ค่าเช่าต่อเดือน: จาก product.monthlyRentalRate (API คืน camelCase + เป็น string "400.00" → coerce)
  // - ค่ามัดจำต่อเครื่อง (PO): จาก product.deposit_amount · แถวที่ติ๊ก "ฟรีมัดจำ" คงเป็น 0
  //   แต่จำยอดไว้ที่ _prev_deposit_amount เผื่อผู้ใช้เอาติ๊กออก (pattern เดิมของหน้านี้)
  // เดิม prefill ทับ prev.rental_items ทั้งอาร์เรย์ → นี่คือต้นเหตุค่าเช่า 400 × 3 แถว = 1,200
  const handleItemProductChange = (index: number, value: string | number) => {
    const productId: number | '' = value === '' || value === null || value === undefined ? '' : Number(value);
    const selectedProduct = productId ? products.find(p => p.id === Number(productId)) : undefined;
    const rate = Number(selectedProduct?.monthlyRentalRate ?? selectedProduct?.monthly_rental_rate);
    const productDeposit = getProductDeposit(productId);

    setFormData(prev => ({
      ...prev,
      rental_items: prev.rental_items.map((item, i) => {
        if (i !== index) return item;  // ห้ามแตะแถวอื่นเด็ดขาด (BUG-U1)
        if (!selectedProduct) return { ...item, product_id: productId };
        return {
          ...item,
          product_id: productId,
          rental_rate_per_month: Number.isFinite(rate) && rate > 0 ? rate : item.rental_rate_per_month,
          deposit_amount: item.deposit_waived ? 0 : productDeposit,
          _prev_deposit_amount: item.deposit_waived ? productDeposit : item._prev_deposit_amount
        };
      })
    }));
    setIsFormDirty(true);

    const errorKey = `item_${index}_product`;
    if (formErrors[errorKey]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      });
    }
  };

  // เพิ่มรายการใหม่
  // BUG-U1: แถวใหม่เริ่มที่ "ว่าง/0" เสมอ — ไม่ copy สินค้า/ค่าเช่า/มัดจำจากแถวอื่นหรือจาก header
  // (parity กับ RentalEditPageV2) · ค่าเช่า/มัดจำจะถูก prefill เมื่อผู้ใช้เลือกสินค้าของแถวนั้นเอง
  const addRentalItem = () => {
    const newItem: RentalItemFormData = {
      id: Date.now().toString(),
      product_id: '',
      device_type_id: '',
      device_id: '',
      sim_id: '', // เพิ่ม sim_id
      quantity: 1,
      rental_rate_per_month: 0,
      deposit_amount: 0,
      deposit_waived: false, // ยกเว้นค่ามัดจำ
      notes: '',
      is_software_only: false, // เช่า Software อย่างเดียว
      // Remote Access (AnyDesk)
      anydesk_id: '',
      remote_access_notes: ''
    };

    setFormData(prev => ({
      ...prev,
      rental_items: [...prev.rental_items, newItem]
    }));
  };

  // ลบรายการ
  const removeRentalItem = (index: number) => {
    if (formData.rental_items.length > 1) {
      const updatedItems = formData.rental_items.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, rental_items: updatedItems }));
    }
  };

  // คำนวณยอดรวม
  const calculateTotals = () => {
    // คำนวณมูลค่าการเช่ารวมตามจำนวนวันจริง
    const totalAmount = formData.rental_items.reduce((sum, item) => {
      const dailyRate = (item.rental_rate_per_month || 0) / 30; // คำนวณค่าเช่าต่อวัน (หาร 30)
      const totalDays = formData.rental_period_days || 0; // ใช้จำนวนวันจากฟอร์มหลัก
      return sum + (dailyRate * (item.quantity || 1) * totalDays);
    }, 0);
    
    const totalDeposit = formData.rental_items.reduce((sum, item) => {
      // นับเฉพาะค่ามัดจำที่ไม่ได้ถูกยกเว้น
      return sum + (item.deposit_waived ? 0 : (item.deposit_amount || 0));
    }, 0);
    
    // คำนวณมูลค่าการเช่าต่อเดือน
    const monthlyRentalValue = formData.rental_items.reduce((sum, item) => {
      return sum + ((item.rental_rate_per_month || 0) * (item.quantity || 1));
    }, 0);
    
    // คำนวณมูลค่าการเช่าต่อปี (12 เดือน)
    const yearlyRentalValue = monthlyRentalValue * 12;
    
    return { totalAmount, totalDeposit, monthlyRentalValue, yearlyRentalValue };
  };

  // ตรวจสอบข้อมูลฟอร์ม
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.rental_number.trim()) {
      errors.rental_number = 'กรุณากรอกเลขที่การเช่า';
    }

    if (!formData.company_id) {
      errors.company_id = 'กรุณาเลือกบริษัท';
    }

    if (!formData.site_id) {
      errors.site_id = 'กรุณาเลือกโครงการ';
    }

    if (!formData.rental_date) {
      errors.rental_date = 'กรุณาเลือกวันที่เช่า';
    }

    // [PO 2026-06-24] "วันที่คาดว่าจะครบสัญญา" ไม่บังคับ + เริ่มต้นเว้นว่าง (ไม่ auto-fill +1ปี แล้ว)
    // = สัญญาเปิด/ยังไม่กำหนด ให้ support สร้างก่อน แล้วเซลมาเติมทีหลังได้.
    // ถ้าระบุ ต้องไม่น้อยกว่าวันที่เช่า (R61: parity กับ backend BUG-074)
    // หมายเหตุ: ห้าม revert กลับเป็น required/auto-fill — เป็นคำสั่ง PO โดยตรง (ไม่ใช่ regression)
    if (formData.expected_return_date && formData.rental_date && formData.expected_return_date < formData.rental_date) {
      errors.expected_return_date = 'วันที่คืนต้องไม่น้อยกว่าวันที่เช่า';
    }

    // [PO 2026-06-24] "วันที่เริ่มเก็บเงิน" ไม่บังคับ — เว้นว่างได้ (NULL → backend proration
    // fallback ใช้วันที่เช่าเป็นวันเริ่มคิดเงิน). ห้าม revert กลับเป็น required (คำสั่ง PO โดยตรง)
    if (formData.billing_start_date && formData.rental_date && formData.billing_start_date < formData.rental_date) {
      errors.billing_start_date = 'วันที่เริ่มเก็บเงินต้องไม่น้อยกว่าวันที่เช่า';
    }
    // ชำระล่วงหน้าถึงวันที่ ต้องไม่ก่อนวันเริ่มเก็บเงิน (parity กับ backend)
    {
      const _prepaidStart = formData.billing_start_date || formData.rental_date;
      if (formData.prepaid_until && _prepaidStart && formData.prepaid_until < _prepaidStart) {
        errors.prepaid_until = 'วันที่ชำระล่วงหน้าถึงต้องไม่น้อยกว่าวันที่เริ่มเก็บเงิน';
      }
    }
    // ตรวจสอบว่า billing_start_date ต้องไม่เกินวันที่คืน (เฉพาะเมื่อระบุวันคืน)
    if (formData.billing_start_date && formData.expected_return_date && formData.billing_start_date > formData.expected_return_date) {
      errors.billing_start_date = 'วันที่เริ่มเก็บเงินต้องไม่เกินวันที่คาดว่าจะคืน';
    }

    // ตรวจสอบรายการอุปกรณ์
    formData.rental_items.forEach((item, index) => {
      // BUG-U1: "สินค้า" เคยเป็น required ที่ระดับสัญญา (dropdown เดียว) — ย้ายมาบังคับรายแถว
      // เพื่อคงคุณภาพข้อมูลเดิม (ทุกรายการต้องมี product_id) แต่ให้เลือกคนละสินค้าได้
      if (!item.product_id) {
        errors[`item_${index}_product`] = `กรุณาเลือกสินค้าสำหรับรายการที่ ${index + 1}`;
      }
      if (!item.is_software_only) {
        if (!item.device_type_id) {
          errors[`item_${index}_device_type`] = `กรุณาเลือกประเภทอุปกรณ์สำหรับรายการที่ ${index + 1}`;
        }
        if (!item.device_id) {
          errors[`item_${index}_device`] = `กรุณาเลือกอุปกรณ์สำหรับรายการที่ ${index + 1}`;
        }
      }
      if (item.rental_rate_per_month < 0) {
        errors[`item_${index}_rate`] = `ค่าเช่าต่อเดือนต้องไม่ติดลบสำหรับรายการที่ ${index + 1}`;
      }
      // ตรวจสอบค่ามัดจำ - อนุญาตให้เป็น 0 ได้ (ไม่ต้องติ๊กยกเว้น)
      if (!item.deposit_waived && item.deposit_amount < 0) {
        errors[`item_${index}_deposit`] = `ค่ามัดจำต้องไม่ติดลบสำหรับรายการที่ ${index + 1}`;
      }

      // ตรวจสอบจำนวนและการซ้ำซ้อนของอุปกรณ์
      if (item.device_id) {
        const selectedDevice = devices.find(d => d.id === item.device_id);
        if (selectedDevice) {
          if (selectedDevice.is_serialized) {
            // อุปกรณ์ที่มี Serial: ห้ามเลือกซ้ำ
            const duplicateItems = formData.rental_items.filter((otherItem, otherIndex) =>
              otherIndex !== index && otherItem.device_id === item.device_id
            );
            if (duplicateItems.length > 0) {
              errors[`item_${index}_device`] = `อุปกรณ์นี้ถูกเลือกแล้วในรายการอื่น ไม่สามารถเลือกซ้ำได้`;
            }
            if (item.quantity > 1) {
              errors[`item_${index}_quantity`] = `อุปกรณ์ที่มี Serial Number เลือกได้เพียง 1 ชิ้น`;
            }
          } else {
            // อุปกรณ์นับจำนวน: ตรวจสอบจำนวนรวม
            const totalUsedInOtherItems = formData.rental_items
              .filter((otherItem, otherIndex) => otherIndex !== index && otherItem.device_id === item.device_id)
              .reduce((sum, otherItem) => sum + (otherItem.quantity || 0), 0);

            const totalRequestedQuantity = totalUsedInOtherItems + (item.quantity || 0);

            if (totalRequestedQuantity > selectedDevice.quantity) {
              errors[`item_${index}_quantity`] = `จำนวนรวมที่เลือก (${totalRequestedQuantity}) เกินจำนวนที่มีอยู่ (${selectedDevice.quantity})`;
            }
          }
        }
      }
    });

    // PO (วงเงินมัดจำ advisory): "ไม่บล็อก" การบันทึกเมื่อเกินวงเงิน —
    // วงเงินมัดจำเป็นแค่ข้อมูลแนะนำ (advisory) ให้ผู้สร้างตัดสินใจเอง + ติ๊กฟรีมัดจำเองได้
    // การเตือน "เกินวงเงิน" แสดงในแผง "วงเงินมัดจำ" (สีแดง) อย่างเดียว ไม่ใส่ลง errors / ไม่ block submit

    // ถ้ามี errors ให้ set inline errors และ scroll to first error
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      // Scroll to first error field
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (errorElement as HTMLInputElement).focus();
      }
      return false;
    }

    setFormErrors({});
    return true;
  };

  // บันทึกข้อมูล — createStatus: 'DRAFT' (default, ไม่จองอุปกรณ์) หรือ 'ACTIVE' (จองอุปกรณ์ทันที)
  const handleSubmit = async (e: React.FormEvent | null, createStatus: 'DRAFT' | 'ACTIVE' = 'DRAFT') => {
    e?.preventDefault();

    if (isSubmittingRef.current) {
      return;  // BUG-S2003-001: กันคลิกซ้ำระหว่างรอ checkCodeExists/POST
    }
    isSubmittingRef.current = true;

    if (!validateForm()) {
      isSubmittingRef.current = false;
      return;
    }

    // ตรวจสอบรหัสซ้ำก่อนบันทึก
    if (formData.rental_number) {
      const codeExists = await rentalService.checkCodeExists(formData.rental_number);
      
      if (codeExists) {
        // สร้างรหัสใหม่
        const oldCode = formData.rental_number;
        const newCode = await codeGeneratorService.generateSingleCode('rental');
        
        // อัพเดท formData
        setFormData(prev => ({
          ...prev,
          rental_number: newCode
        }));
        
        // แสดงข้อความเตือน
        setCodeError(`รหัส ${oldCode} ไม่ว่างเนื่องจากมีการใช้งานแล้ว กรุณาบันทึกใหม่`);
        isSubmittingRef.current = false;
        return;
      }
    }

    // ล้างข้อความเตือนถ้าไม่มีปัญหา
    setCodeError('');
    
    try {
      setLoading(true);
      
      // เตรียมข้อมูลสำหรับส่ง API
      // total_amount = run-rate ต่อเดือน (มูลค่าจริงของ subscription) — เงินจริงคิดจาก proration ตอนออก invoice
      const { totalDeposit, monthlyRentalValue } = calculateTotals();

      const userId = getUserId();

      // GAP-2: ใช้ userId ตรง ไม่ fallback เป็น hardcoded ID
      const finalUserId = userId;
      
      const rentalData = {
        rental_number: formData.rental_number,
        site_id: formData.site_id,
        company_id: formData.company_id,
        rental_date: new Date(formData.rental_date),
        billing_start_date: formData.billing_start_date ? new Date(formData.billing_start_date) : null,
        // ชำระล่วงหน้า: ไม่มีค่า = ไม่ส่ง key (สัญญาใหม่ส่วนใหญ่ไม่ชำระล่วงหน้า)
        ...(formData.prepaid_until ? { prepaid_until: formData.prepaid_until } : {}),
        ...(formData.prepaid_note.trim() ? { prepaid_note: formData.prepaid_note.trim() } : {}),
        expected_return_date: formData.expected_return_date ? new Date(formData.expected_return_date) : null,
        total_amount: monthlyRentalValue,
        deposit_amount: totalDeposit,
        notes: formData.notes,
        // New fields for data migration
        delivery_date: formData.delivery_date ? new Date(formData.delivery_date) : null,
        created_by: finalUserId, // เพิ่มฟิลด์ created_by
        rental_items: formData.rental_items.map(item => ({
          product_id: item.product_id || null, // BUG-U1: สินค้าของ "แถวนั้น" (เดิมยัด product_id เดียวจาก header ลงทุกแถว)
          device_type_id: item.is_software_only ? null : item.device_type_id,
          device_id: item.is_software_only ? null : item.device_id,
          sim_id: item.sim_id || null, // เพิ่ม sim_id
          quantity: item.quantity,
          rental_rate_per_month: item.rental_rate_per_month,
          // total_amount = ค่าเช่าต่อเดือน (run-rate) — ไม่คูณระยะเวลา; เงินจริงคิดจาก proration ตอนออก invoice
          total_amount: (item.rental_rate_per_month || 0) * (item.quantity || 1),
          deposit_amount: item.deposit_waived ? 0 : item.deposit_amount, // ถ้ายกเว้นค่ามัดจำให้เป็น 0
          deposit_waived: item.deposit_waived, // ส่งข้อมูลการยกเว้นค่ามัดจำ
          is_software_only: item.is_software_only, // เช่า Software อย่างเดียว
          notes: item.notes,
          // Remote Access (AnyDesk) — ต่อเครื่อง (create ส่งได้ทั้ง 3 fields ตรงๆ)
          anydesk_id: item.anydesk_id?.trim() || null,
          remote_access_notes: item.remote_access_notes?.trim() || null
        })),
        // ส่ง status เฉพาะเมื่อต้องการ ACTIVE (จองอุปกรณ์ทันที); DRAFT = default ของ backend
        ...(createStatus === 'ACTIVE' ? { status: 'ACTIVE' as const } : {})
      };
      
      await rentalService.createRental(rentalData);

      setIsFormDirty(false); // ปิดการเตือนก่อน navigate
      // BUG-FE-RENT-CREATE-MSG FIX (arity): showAlert คือ (title, message, type)
      // เดิมเรียกด้วย 2 args → 'success'/'error' ตกไปอยู่ช่อง message และ type กลายเป็น 'info' เสมอ
      showAlert('สำเร็จ', 'บันทึกข้อมูลการเช่าเรียบร้อยแล้ว', 'success');
      navigate('/rentals/list');

    } catch (error: any) {
      console.error('Error creating rental:', error);
      // BUG-FE-RENT-CREATE-MSG FIX (error path): backend ห่อ error ไว้ที่ data.error.*
      // เดิมอ่าน data.message (undefined เสมอ) → ข้อความจริงของ BE หายไป ผู้ใช้เห็นแต่ข้อความกลางๆ
      // เช่น "อุปกรณ์ SN-S216B ถูกใช้งานอยู่ใน rental RNT-20260814-801 แล้ว" ที่บอกว่าติดที่ไหน
      // มิเรอร์ pattern เดียวกับ BUG-FE-RETURN-MSG (RentalViewPage handleApprove)
      // BUG-S2158-001: 422 จาก validator ระดับ body (เช่น "ระยะเวลาการเช่าต้องไม่เกิน 10 ปี") มี
      // user_message เป็นประโยคกลาง "กรุณาตรวจสอบข้อมูล…" เสมอ — อ่าน user_message ก่อนจึงทิ้งเหตุผลจริง
      // → ใช้ getApiErrorMessage (field_errors → error.message → user_message) ตัวกลางของระบบ
      showAlert(
        'เกิดข้อผิดพลาด',
        getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการบันทึกข้อมูล'),
        'error'
      );
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;  // BUG-S2003-001: ปลด lock ทุกกรณี (สำเร็จ/ล้มเหลว)
    }
  };

  // [PO 2026-06-24] สรุปยอดล่างสุดแสดง "ค่าเช่ารวม/เดือน" (= monthlyRentalValue) แทนค่ามัดจำ
  // (มัดจำมี panel ด้านบนแล้ว). totalDeposit ยังใช้คำนวณ "ใช้ไปแล้ว" ของ panel วงเงินมัดจำด้านบน
  const { totalDeposit, monthlyRentalValue } = calculateTotals();

  // วงเงินมัดจำเริ่มต้น (PO): บริษัทที่ยังไม่ตั้งเพดาน (NULL/0) → ใช้ค่าเริ่มต้น 40,000
  const DEFAULT_DEPOSIT_CAP = 40000;

  // คำนวณผลกระทบต่อวงเงินมัดจำ
  const calculateDepositImpact = () => {
    if (!companyDepositInfo) return null;

    // เพดานที่ใช้จริง: ค่าที่บริษัทตั้งไว้ ถ้ายังไม่ตั้ง → default 40,000 (panel แสดงเสมอ)
    const isDefaultCap = !(companyDepositInfo.deposit_limit > 0);
    const effectiveLimit = isDefaultCap ? DEFAULT_DEPOSIT_CAP : companyDepositInfo.deposit_limit;
    const newTotalDeposit = companyDepositInfo.total_deposit + totalDeposit;
    const remainingAfter = effectiveLimit - newTotalDeposit;
    const usageAfter = (newTotalDeposit / effectiveLimit) * 100;

    return {
      effectiveLimit,
      isDefaultCap,
      newTotalDeposit,
      remainingAfter,
      usageAfter,
      isOverLimit: newTotalDeposit > effectiveLimit,
      isNearLimit: usageAfter >= 90
    };
  };

  const depositImpact = calculateDepositImpact();

  // 1 เครื่อง = 1 สัญญา (มติ 10 ต.ค. 2026) — เตือนอย่างเดียว ไม่บล็อกบันทึก (ตั้งใจรวมเป็นชุดได้)
  const serializedDeviceCount = countSerializedDevices(formData.rental_items, devices);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Unsaved Changes Confirm Modal */}
      <ConfirmModal
        isOpen={showModal}
        onClose={onCancelLeave}
        onConfirm={onConfirmLeave}
        pushHistoryOnOpen={false}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <ConfirmModal
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={() => { setShowBackConfirm(false); setIsFormDirty(false); navigate('/rentals/list'); }}
        title="ข้อมูลยังไม่ได้บันทึก"
        message="คุณมีข้อมูลที่ยังไม่ได้บันทึก หากออกจากหน้านี้ข้อมูลที่กรอกจะหายไป ต้องการออกจากหน้านี้หรือไม่?"
        confirmText="ออกจากหน้านี้"
        cancelText="อยู่ต่อ"
        isDanger={true}
      />
      <div className="w-full">
        {/* Executive Navy Theme Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-primary-600 flex items-center justify-center flex-shrink-0 shadow-sm">
              <RiAddCircleLine className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-secondary-900 leading-tight">สร้างรายการเช่าใหม่</h1>
              <p className="text-xs text-secondary-500 mt-0.5">กรอกข้อมูลการเช่าอุปกรณ์สำหรับลูกค้า</p>
            </div>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e, 'DRAFT')} onChange={() => setIsFormDirty(true)} noValidate className="space-y-6">
          {/* ข้อมูลทั่วไป */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiInformationLine className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-semibold text-secondary-800">ข้อมูลทั่วไป</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-6">
                {/* เลขที่การเช่า */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <ValidatedInput
                    label="เลขที่การเช่า"
                    name="rental_number"
                    value={formData.rental_number}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, rental_number: e.target.value }));
                      if (formErrors.rental_number) {
                        setFormErrors(prev => ({ ...prev, rental_number: '' }));
                      }
                    }}
                    required
                    readOnly
                    placeholder="กำลังสร้างรหัส..."
                  />
                  {formErrors.rental_number && (
                    <p className="mt-1 text-sm text-danger-600">{formErrors.rental_number}</p>
                  )}
                  {codeError && (
                    <p className="mt-1 text-sm text-danger-600 flex items-center">
                      <RiErrorWarningLine className="mr-1" />
                      {codeError}
                    </p>
                  )}
                </div>

                {/* บริษัท */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="company_id" className="block text-sm font-medium text-secondary-700">
                    บริษัท <span className="text-danger-500">*</span>
                  </label>
                  <SearchableSelect
                    options={companies.map(company => ({
                      value: company.id,
                      label: `${company.code} - ${company.name}`
                    }))}
                    value={formData.company_id || ''}
                    onChange={(value) => {
                      setFormData(prev => ({
                        ...prev,
                        company_id: Number(value),
                        site_id: '' // reset site เมื่อเปลี่ยนบริษัท
                      }));
                      if (formErrors.company_id) {
                        setFormErrors(prev => ({ ...prev, company_id: '' }));
                      }
                    }}
                    placeholder="-- เลือกบริษัท --"
                    searchPlaceholder="ค้นหาบริษัท..."
                  />
                  {formErrors.company_id && (
                    <p className="mt-1 text-sm text-danger-600">{formErrors.company_id}</p>
                  )}
                </div>

                {/* โครงการ */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="site_id" className="block text-sm font-medium text-secondary-700">
                    โครงการ <span className="text-danger-500">*</span>
                  </label>
                  <SearchableSelect
                    options={filteredSites.map(site => ({
                      value: site.id,
                      label: `${site.code} - ${site.name}`
                    }))}
                    value={formData.site_id || ''}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, site_id: Number(value) }));
                      if (formErrors.site_id) {
                        setFormErrors(prev => ({ ...prev, site_id: '' }));
                      }
                    }}
                    placeholder="-- เลือกโครงการ --"
                    searchPlaceholder="ค้นหาโครงการ..."
                    disabled={!formData.company_id}
                  />
                  {formErrors.site_id && (
                    <p className="mt-1 text-sm text-danger-600">{formErrors.site_id}</p>
                  )}
                </div>

                {/* BUG-U1: dropdown "สินค้า" ระดับสัญญาถูกย้ายไปอยู่ในแต่ละรายการอุปกรณ์ด้านล่าง
                    (เหมือนหน้าแก้ไขการเช่า) — ที่นี่มีไว้ตัวเดียวทำให้ prefill ค่าเช่า/มัดจำทับทุกแถว */}

                {/* วันที่เช่า */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="rental_date" className="block text-sm font-medium text-secondary-700">
                    วันที่เช่า <span className="text-danger-500">*</span>
                  </label>
                  <input
                    type="date"
                    id="rental_date"
                    value={formData.rental_date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, rental_date: e.target.value }));
                      if (formErrors.rental_date) {
                        setFormErrors(prev => ({ ...prev, rental_date: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.rental_date && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    aria-invalid={Boolean(formErrors.rental_date)}
                    aria-describedby={formErrors.rental_date ? 'rental_date-error' : undefined}
                  />
                  {formErrors.rental_date && (
                    <p id="rental_date-error" className="mt-1 text-sm text-danger-600">{formErrors.rental_date}</p>
                  )}
                </div>

                {/* วันที่คาดว่าจะครบสัญญา (ไม่บังคับ — PO 2026-06-24 · เริ่มต้นเว้นว่าง=สัญญาเปิด · ห้าม revert/auto-fill) */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="expected_return_date" className="block text-sm font-medium text-secondary-700">
                    วันที่คาดว่าจะครบสัญญา <span className="text-secondary-500 font-normal">(ไม่บังคับ)</span>
                  </label>
                  <input
                    type="date"
                    id="expected_return_date"
                    name="expected_return_date"
                    value={formData.expected_return_date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, expected_return_date: e.target.value }));
                      if (formErrors.expected_return_date) {
                        setFormErrors(prev => ({ ...prev, expected_return_date: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.expected_return_date && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    aria-invalid={Boolean(formErrors.expected_return_date || dateError)}
                    aria-describedby={(formErrors.expected_return_date || dateError) ? 'expected_return_date-error' : undefined}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    ครบกำหนดสัญญา · เว้นว่าง = สัญญาเปิด (ยังไม่กำหนดวันครบ · เติมภายหลังได้)
                  </p>
                  {formErrors.expected_return_date && (
                    <p id="expected_return_date-error" className="mt-1 text-sm text-danger-600">{formErrors.expected_return_date}</p>
                  )}
                  {dateError && !formErrors.expected_return_date && (
                    <p id="expected_return_date-error" className="mt-1 text-sm text-danger-600">{dateError}</p>
                  )}
                </div>

                {/* วันที่เริ่มเก็บเงิน */}
                <div className="sm:col-span-1 lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    วันที่เริ่มเก็บเงิน <span className="text-secondary-500 font-normal">(ไม่บังคับ)</span>
                  </label>
                  <div className="relative">
                    <RiCalendarLine className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400 w-5 h-5" />
                    <input
                      type="date"
                      name="billing_start_date"
                      aria-label="วันเริ่มเรียกเก็บ"
                      value={formData.billing_start_date}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, billing_start_date: e.target.value }));
                        if (formErrors.billing_start_date) {
                          setFormErrors(prev => ({ ...prev, billing_start_date: '' }));
                        }
                      }}
                      min={formData.rental_date}
                      className={cn('input pl-10', formErrors.billing_start_date && 'border-danger-500 bg-danger-50')}
                      aria-invalid={Boolean(formErrors.billing_start_date)}
                      aria-describedby={formErrors.billing_start_date ? 'billing_start_date-error' : undefined}
                    />
                  </div>
                  {formErrors.billing_start_date && (
                    <p id="billing_start_date-error" className="mt-1 text-sm text-danger-600">{formErrors.billing_start_date}</p>
                  )}
                  <p className="mt-1 text-xs text-secondary-500">
                    หากมีช่วงทดลองใช้ฟรี ให้ระบุวันที่เริ่มเก็บเงินจริง · เว้นว่างได้ (ระบบจะใช้วันที่เช่า)
                  </p>
                </div>

                {/* ระยะเวลา (วัน) — แสดงเฉพาะเมื่อระบุวันคืน */}
                {formData.expected_return_date && (
                <div className="sm:col-span-1 lg:col-span-2">
                  <label htmlFor="rental_period_days" className="block text-sm font-medium text-secondary-700">
                    ระยะเวลา (วัน)
                  </label>
                  <input
                    type="number"
                    id="rental_period_days"
                    value={formData.rental_period_days}
                    readOnly
                    className={cn('input mt-1 bg-secondary-50 text-secondary-500', loading && 'cursor-not-allowed')}
                  />
                  <p className="mt-1 text-sm text-secondary-500">คำนวณอัตโนมัติจากวันที่เช่าและวันที่คืน</p>
                </div>
                )}

                {/* แสดงข้อมูล Free Trial ถ้ามี */}
                {formData.billing_start_date && formData.rental_date &&
                 new Date(formData.billing_start_date) > new Date(formData.rental_date) && (
                  <div className="sm:col-span-2 lg:col-span-6 bg-info-50 border border-info-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-info-700">
                      <RiGiftLine className="w-5 h-5" />
                      <span className="font-medium">
                        ทดลองใช้ฟรี {Math.ceil((new Date(formData.billing_start_date).getTime() - new Date(formData.rental_date).getTime()) / (1000 * 60 * 60 * 24))} วัน
                      </span>
                    </div>
                  </div>
                )}

                {/* ชำระล่วงหน้าถึงวันที่ — วางบิลข้ามรอบที่ลูกค้าจ่ายแล้ว */}
                <PrepaidUntilFields
                  prepaidUntil={formData.prepaid_until}
                  prepaidNote={formData.prepaid_note}
                  minDate={formData.billing_start_date || formData.rental_date}
                  disabled={loading}
                  error={formErrors.prepaid_until}
                  onChange={(next) => {
                    setFormData(prev => ({ ...prev, ...next }));
                    if (formErrors.prepaid_until) setFormErrors(prev => ({ ...prev, prepaid_until: '' }));
                  }}
                />

                {/* วันที่ส่งมอบอุปกรณ์ */}
                <div className="sm:col-span-1 lg:col-span-3">
                  <label htmlFor="delivery_date" className="block text-sm font-medium text-secondary-700">
                    วันที่ส่งมอบอุปกรณ์
                  </label>
                  <input
                    type="date"
                    id="delivery_date"
                    value={formData.delivery_date}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, delivery_date: e.target.value }));
                    }}
                    className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
                  />
                </div>

                {/* หมายเหตุ */}
                <div className="sm:col-span-2 lg:col-span-6">
                  <label htmlFor="notes" className="block text-sm font-medium text-secondary-700">
                    หมายเหตุ
                  </label>
                  <textarea
                    id="notes"
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, notes: e.target.value }));
                      if (formErrors.notes) {
                        setFormErrors(prev => ({ ...prev, notes: '' }));
                      }
                    }}
                    className={cn('input mt-1', formErrors.notes && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                    placeholder="หมายเหตุเพิ่มเติม..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* แผง "วงเงินมัดจำ" (advisory) — PO:
              - แสดงทันทีที่เลือกบริษัท · เพดาน = ค่าที่บริษัทตั้ง, ถ้ายังไม่ตั้ง (NULL/0) → ค่าเริ่มต้น 40,000
              - แสดง สูงสุด / ใช้ไป / เหลือ แบบ live
                · ใช้ไป (used) = total_deposit (สัญญา DRAFT+ACTIVE เดิม) + ผลรวมมัดจำ rental_items ในฟอร์มนี้ที่ "ไม่ติ๊กฟรี" (totalDeposit)
                · เหลือ (remaining) = เพดาน − ใช้ไป → ถ้า used > เพดาน แสดง "เกินวงเงินมัดจำ (เกิน X บาท)" สีแดง
              - advisory ล้วน: เกินวงเงินไม่บล็อกการบันทึก */}
          {formData.company_id && companyDepositInfo && depositImpact && (
            <div className={cn(
              'border-2 rounded-lg shadow-sm mb-6',
              depositImpact.isOverLimit ? 'bg-danger-50 border-danger-400' : 'bg-warning-50 border-warning-300'
            )}>
              <div className="px-4 py-5 sm:p-6">
                <div className="flex items-center gap-2 pb-2 border-b border-blue-200 mb-4">
                  <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center shadow-sm">
                    <RiWalletLine className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-blue-800">
                    วงเงินมัดจำของบริษัท: {companies.find(c => c.id === formData.company_id)?.name}
                  </h2>
                </div>
                <div className="flex flex-wrap items-end gap-x-10 gap-y-3">
                  {/* สูงสุด (เพดานวงเงิน) */}
                  <div>
                    <p className="text-sm text-secondary-600">
                      วงเงินมัดจำสูงสุด
                      {depositImpact.isDefaultCap && <span className="text-xs text-secondary-400"> (ค่าเริ่มต้น)</span>}
                    </p>
                    <p className="text-3xl font-bold text-secondary-800">
                      {depositImpact.effectiveLimit.toLocaleString()} <span className="text-base font-medium">บาท</span>
                    </p>
                  </div>

                  {/* ใช้ไปแล้ว (live = เดิม + ในฟอร์มนี้) — สีเด่น แดงเมื่อเกิน */}
                  <div>
                    <p className="text-sm text-secondary-600">ใช้ไปแล้ว</p>
                    <p className={cn(
                      'text-3xl font-extrabold',
                      depositImpact.isOverLimit ? 'text-danger-600' : 'text-success-600'
                    )}>
                      {depositImpact.newTotalDeposit.toLocaleString()} <span className="text-base font-medium">บาท</span>
                    </p>
                  </div>

                  {depositImpact.isOverLimit && (
                    <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-bold bg-danger-600 text-white">
                      <RiErrorWarningLine className="w-4 h-4 mr-1" />
                      เกินวงเงิน {Math.abs(depositImpact.remainingAfter).toLocaleString()} บาท (ยังบันทึกได้)
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* รายการอุปกรณ์ */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 flex-1">
                  <div className="w-8 h-8 bg-gradient-to-br from-success-500 to-success-600 rounded-lg flex items-center justify-center shadow-sm">
                    <RiListCheck className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-secondary-800">รายการอุปกรณ์</h2>
                </div>
                <button
                  type="button"
                  onClick={addRentalItem}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 min-h-[44px] sm:min-h-[36px] rounded-md text-sm font-medium flex items-center ml-4"
                >
                  <RiAddLine className="h-4 w-4 mr-2" />
                  เพิ่มรายการ
                </button>
              </div>

              {serializedDeviceCount > 1 && (
                <div
                  role="status"
                  className="mb-4 flex items-start gap-2 bg-warning-50 border border-warning-200 rounded-lg p-3"
                >
                  <RiAlertLine className="w-5 h-5 text-warning-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-warning-800">{MULTI_SERIALIZED_WARNING}</p>
                </div>
              )}

              <div className="space-y-6">
                {formData.rental_items.map((item, index) => (
                  <div key={item.id} className="border border-gray-200 rounded-lg p-4 bg-slate-50">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-md font-medium text-secondary-900">
                        รายการที่ {index + 1}
                      </h3>
                      {formData.rental_items.length > 1 && (
                        // BUG-S083-DELTARGET: this was a bare 20×20 icon — below the 44px touch
                        // target on a phone, and it deletes a row the user already filled in,
                        // with no undo. It also had no accessible name, so a screen reader
                        // announced only "button". Pad it to 44px and name it.
                        <button
                          type="button"
                          onClick={() => removeRentalItem(index)}
                          aria-label={`ลบรายการที่ ${index + 1}`}
                          title={`ลบรายการที่ ${index + 1}`}
                          className="inline-flex items-center justify-center h-11 w-11 -mr-2 rounded-md text-danger-600 hover:text-red-800 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-danger-500"
                        >
                          <RiDeleteBinLine className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-6">
                      {/* BUG-U1 / GAP-29: สินค้าของ "รายการนี้" — เลือกได้คนละตัว/คนละราคาต่อแถว
                          (เดิมเลือกครั้งเดียวที่หัวสัญญาแล้ว prefill ทับทุกแถว → ยอดรวมบวกซ้ำ) */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          สินค้า <span className="text-danger-500">*</span>
                        </label>
                        <SearchableSelect
                          options={products.map(product => ({
                            value: product.id,
                            label: `${product.code} - ${product.name}`
                          }))}
                          value={item.product_id || ''}
                          onChange={(value) => handleItemProductChange(index, value)}
                          placeholder="-- เลือกสินค้า --"
                          searchPlaceholder="ค้นหาสินค้า..."
                        />
                        {formErrors[`item_${index}_product`] && (
                          <p className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_product`]}</p>
                        )}
                      </div>

                      {/* Checkbox Software Only */}
                      <div className="sm:col-span-1 lg:col-span-4 flex items-end pb-1">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            id={`sw_only_${index}`}
                            checked={item.is_software_only || false}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              const updatedItems = [...formData.rental_items];
                              updatedItems[index] = {
                                ...updatedItems[index],
                                is_software_only: checked,
                                ...(checked
                                  ? {
                                      _prev_device_type_id: updatedItems[index].device_type_id,
                                      _prev_device_id: updatedItems[index].device_id,
                                      device_type_id: '',
                                      device_id: ''
                                    }
                                  : {
                                      device_type_id: updatedItems[index]._prev_device_type_id || '',
                                      device_id: updatedItems[index]._prev_device_id || ''
                                    })
                              };
                              setFormData(prev => ({ ...prev, rental_items: updatedItems }));
                              setIsFormDirty(true);
                            }}
                            className="w-4 h-4 text-purple-600 border-secondary-300 rounded focus:ring-purple-500 cursor-pointer"
                          />
                          <label htmlFor={`sw_only_${index}`} className="text-sm font-medium text-secondary-700 cursor-pointer select-none">
                            เช่า Software อย่างเดียว (ลูกค้าใช้เครื่องตัวเอง)
                          </label>
                          {item.is_software_only && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-700">
                              SW Only
                            </span>
                          )}
                        </div>
                      </div>

                      {/* ประเภทอุปกรณ์ + อุปกรณ์ (ซ่อนเมื่อเป็น SW Only) */}
                      {!item.is_software_only && (
                        <>
                      {/* ประเภทอุปกรณ์ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ประเภทอุปกรณ์ <span className="text-danger-500">*</span>
                        </label>
                        <SearchableSelect
                          options={deviceTypes.map(deviceType => ({
                            value: deviceType.id,
                            label: `${deviceType.code} - ${deviceType.name}`
                          }))}
                          value={item.device_type_id || ''}
                          onChange={(value) => {
                            updateRentalItem(index, 'device_type_id', Number(value));
                          }}
                          placeholder={loadingDeviceTypes ? "กำลังโหลดประเภทอุปกรณ์..." : "-- เลือกประเภทอุปกรณ์ --"}
                          searchPlaceholder="ค้นหาประเภทอุปกรณ์..."
                          disabled={loadingDeviceTypes}
                        />
                        {/* BUG-178 FIX: per-field loading indicator */}
                        {loadingDeviceTypes && (
                          <p className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            กำลังโหลดข้อมูล...
                          </p>
                        )}
                        {formErrors[`item_${index}_device_type`] && (
                          <p className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_device_type`]}</p>
                        )}
                      </div>

                      {/* อุปกรณ์ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          อุปกรณ์ <span className="text-danger-500">*</span>
                        </label>
                        <SearchableSelect
                          options={(() => {
                            // กรองอุปกรณ์ตาม device_type_id ของรายการนี้โดยตรง
                            const filtered = devices.filter(device => {
                              if (device.device_type_id !== item.device_type_id) return false;
                              if (!(device.status === 'AVAILABLE' || device.status === 'available' || device.status === 'พร้อมใช้งาน')) return false;
                              // R4U-2: เครื่องที่ยังมีใบตามเก็บค้าง (ของอาจยังอยู่กับลูกค้า แม้สถานะ drift เป็น AVAILABLE)
                              // ไม่เสนอ — ไม่งั้นเลือกได้แล้วไปโดน 400 ด่าน L2 ตอนบันทึก · เว้นเครื่องที่แถวนี้เลือกไว้แล้ว
                              // (ค่าที่ prefill มา) เพื่อไม่ให้ช่องกลับเป็นว่างเงียบ ๆ — backend ยังเป็นด่านสุดท้าย
                              if (hasPendingReturn(device) && device.id !== item.device_id) return false;

                              // ตรวจสอบการใช้งานในรายการอื่นๆ
                              const usedInOtherItems = formData.rental_items
                                .filter((_, i) => i !== index) // ยกเว้นรายการปัจจุบัน
                                .filter(otherItem => otherItem.device_id === device.id);
                              
                              if (device.is_serialized) {
                                // อุปกรณ์ที่มี Serial: ห้ามเลือกซ้ำ
                                return usedInOtherItems.length === 0;
                              } else {
                                // อุปกรณ์นับจำนวน: ตรวจสอบจำนวนรวม
                                const totalUsedQuantity = usedInOtherItems.reduce((sum, usedItem) => sum + (usedItem.quantity || 0), 0);
                                const availableQuantity = device.quantity - totalUsedQuantity;
                                return availableQuantity > 0;
                              }
                            });
                            
                            
                            return filtered.map(device => {
                              // คำนวณจำนวนที่เหลือสำหรับอุปกรณ์นับจำนวน
                              if (!device.is_serialized) {
                                const usedInOtherItems = formData.rental_items
                                  .filter((_, i) => i !== index)
                                  .filter(otherItem => otherItem.device_id === device.id);
                                const totalUsedQuantity = usedInOtherItems.reduce((sum, usedItem) => sum + (usedItem.quantity || 0), 0);
                                const availableQuantity = device.quantity - totalUsedQuantity;
                                
                                // P-S475-1: เตือนเครื่องที่มีเคสซ่อมค้าง — ยังเลือกได้ แต่ต้องเห็นก่อนส่งมอบ
                                // R3U-06: ป้ายจาก helper กลางเดียวกับหน้าแก้ไข
                                // fix ค: เครื่องที่ยังผูกกับสัญญาอื่นอยู่ (rental_hold) ต้องเห็นก่อนเลือก — ฟอร์มสร้างใหม่
                                // ไม่มีสัญญาของตัวเอง (ไม่ส่ง currentRentalId) hold ที่เจอจึงเป็นของสัญญาอื่นเสมอ
                                return {
                                  value: device.id,
                                  label: withRentalHoldLabel(
                                    deviceOptionLabel(device, {
                                      availableQuantity,
                                      openMaintenanceCount: (device as any).open_maintenance_count,
                                    }),
                                    device
                                  ),
                                };
                              }

                              return {
                                value: device.id,
                                label: withRentalHoldLabel(
                                  deviceOptionLabel(device, {
                                    openMaintenanceCount: (device as any).open_maintenance_count,
                                  }),
                                  device
                                ),
                              };
                            });
                          })()}
                          value={item.device_id || ''}
                          onChange={(value) => updateRentalItem(index, 'device_id', Number(value))}
                          placeholder={loadingDevices ? "กำลังโหลดอุปกรณ์..." : !item.device_type_id ? "-- กรุณาเลือกประเภทอุปกรณ์ก่อน --" : "-- เลือกอุปกรณ์ --"}
                          searchPlaceholder="ค้นหาอุปกรณ์..."
                          disabled={!item.device_type_id || loadingDevices}
                        />
                        {/* BUG-178 FIX: per-field loading indicator */}
                        {loadingDevices && (
                          <p className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            กำลังโหลดข้อมูล...
                          </p>
                        )}
                        {formErrors[`item_${index}_device`] && (
                          <p className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_device`]}</p>
                        )}
                        {/* fix ค: เครื่องที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
                        {(() => {
                          const selectedDevice = devices.find(d => d.id === item.device_id);
                          const hold = rentalHoldFor(selectedDevice);
                          if (!hold) return null;
                          return (
                            <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                              {hold.message}
                            </p>
                          );
                        })()}
                      </div>
                        </>
                      )}

                      {/* ซิมการ์ด */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ซิมการ์ด (ถ้ามี)
                        </label>
                        <SearchableSelect
                          options={availableSims.map(sim => {
                            const base = {
                              value: sim.id,
                              // R4U-3: helper กลาง — ซิมที่ operator ว่างห้ามโชว์ "เบอร์ - null (รายเดือน)"
                              label: simOptionLabel(sim)
                            };
                            // fix ค: ซิมที่ยังผูกกับสัญญาอื่นอยู่ (rental_hold) ต้องเห็นก่อนเลือก
                            return { ...base, label: withRentalHoldLabel(base.label, sim) };
                          })}
                          value={item.sim_id || ''}
                          onChange={(value) => updateRentalItem(index, 'sim_id', value ? Number(value) : '')}
                          placeholder={loadingSims ? "กำลังโหลดซิมการ์ด..." : "-- เลือกซิมการ์ด --"}
                          searchPlaceholder="ค้นหาเบอร์โทรศัพท์..."
                          disabled={loadingSims}
                        />
                        {/* BUG-178 FIX: per-field loading indicator */}
                        {loadingSims ? (
                          <p className="mt-1 text-xs text-blue-500 flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                            กำลังโหลดข้อมูล...
                          </p>
                        ) : (
                          <p className="mt-1 text-sm text-secondary-500">เลือกซิมการ์ดสำหรับอุปกรณ์นี้ (ถ้าต้องการ)</p>
                        )}
                        {/* fix ค: ซิมที่เลือกไว้ยังผูกกับสัญญาอื่นที่ยังไม่จบอยู่ — ข้อความไทยจาก backend ตรง ๆ */}
                        {(() => {
                          const selectedSim = sims.find(s => s.id === item.sim_id);
                          const hold = rentalHoldFor(selectedSim);
                          if (!hold) return null;
                          return (
                            <p className="mt-1 text-xs text-info-700 bg-info-50 border border-info-200 rounded px-2 py-1">
                              {hold.message}
                            </p>
                          );
                        })()}
                      </div>

                      {/* จำนวน */}
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700">
                          จำนวน
                        </label>
                        <input
                          type="number"
                          min="1"
                          aria-label={`จำนวน รายการที่ ${index + 1}`}
                          max={(() => {
                            if (!item.device_id) return undefined;

                            const selectedDevice = devices.find(d => d.id === item.device_id);
                            if (!selectedDevice) return undefined;

                            if (selectedDevice.is_serialized) {
                              return 1; // อุปกรณ์ที่มี Serial เลือกได้เพียง 1 ชิ้น
                            } else {
                              // อุปกรณ์นับจำนวน: คำนวณจำนวนที่เหลือ
                              const usedInOtherItems = formData.rental_items
                                .filter((_, i) => i !== index)
                                .filter(otherItem => otherItem.device_id === selectedDevice.id);
                              const totalUsedQuantity = usedInOtherItems.reduce((sum, usedItem) => sum + (usedItem.quantity || 0), 0);
                              return selectedDevice.quantity - totalUsedQuantity;
                            }
                          })()}
                          value={item.quantity}
                          onChange={(e) => updateRentalItem(index, 'quantity', Number(e.target.value))}
                          disabled={!item.device_id || loading}
                          className={cn('input mt-1', formErrors[`item_${index}_quantity`] && 'border-danger-500', (!item.device_id || loading) && 'bg-secondary-50 cursor-not-allowed')}
                          aria-invalid={Boolean(formErrors[`item_${index}_quantity`])}
                          aria-describedby={formErrors[`item_${index}_quantity`] ? `item_${index}_quantity-error` : undefined}
                        />
                        {/* BUG-FE-QTYERR-SILENT FIX (same omission as Edit): the key only
                            drove the red border, so a blocked save gave no reason. The
                            advisory stock hint below is NOT a substitute — it renders the
                            remaining count, not the reason the save was rejected. */}
                        {formErrors[`item_${index}_quantity`] && (
                          <p id={`item_${index}_quantity-error`} className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_quantity`]}</p>
                        )}
                        {item.device_id && (() => {
                          const selectedDevice = devices.find(d => d.id === item.device_id);
                          if (selectedDevice && selectedDevice.is_serialized) {
                            return <p className="mt-1 text-sm text-secondary-500">อุปกรณ์นี้มี Serial Number เลือกได้เพียง 1 ชิ้น</p>;
                          } else if (selectedDevice && !selectedDevice.is_serialized) {
                            const usedInOtherItems = formData.rental_items
                              .filter((_, i) => i !== index)
                              .filter(otherItem => otherItem.device_id === selectedDevice.id);
                            const totalUsedQuantity = usedInOtherItems.reduce((sum, usedItem) => sum + (usedItem.quantity || 0), 0);
                            const availableQuantity = selectedDevice.quantity - totalUsedQuantity;
                            return <p className="mt-1 text-sm text-secondary-500">เลือกได้สูงสุด {availableQuantity} {selectedDevice.unit}</p>;
                          }
                          return null;
                        })()}
                      </div>

                      {/* ค่าเช่าต่อเดือน */}
                      <div className="sm:col-span-1 lg:col-span-1">
                        <label className="block text-sm font-medium text-secondary-700">
                          ค่าเช่าต่อเดือน <span className="text-danger-500">*</span>
                          {/* user feedback 2026-09-08: ระบบรับ 0 ได้อยู่แล้ว แต่ดอกจันทำให้เข้าใจว่าต้องมากกว่า 0
                              → บอกให้ชัดแบบเดียวกับช่องค่ามัดจำข้าง ๆ */}
                          <span className="text-secondary-500 text-sm"> - ใส่ 0 ได้</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label={`ค่าเช่าต่อเดือน รายการที่ ${index + 1}`}
                          value={item.rental_rate_per_month}
                          // OBS-S37-001 FIX (S85): select-all on focus — field default "0",
                          // คลิกแล้วพิมพ์ต่อจะได้ "05000"; select-all ให้พิมพ์ทับ → "5000".
                          // ไม่แตะ onChange/value → ไม่กระทบ decimal entry "0.5"
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value >= 0 || e.target.value === '') {
                              updateRentalItem(index, 'rental_rate_per_month', value);
                            }
                          }}
                          className={cn('input mt-1', formErrors[`item_${index}_rate`] && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                          aria-invalid={Boolean(formErrors[`item_${index}_rate`])}
                          aria-describedby={formErrors[`item_${index}_rate`] ? `item_${index}_rate-error` : undefined}
                        />
                        {formErrors[`item_${index}_rate`] && (
                          <p id={`item_${index}_rate-error`} className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_rate`]}</p>
                        )}
                      </div>

                      {/* ค่ามัดจำ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          ค่ามัดจำ (บาท) <span className="text-secondary-500 text-sm">- สามารถเป็น 0 ได้</span>
                        </label>
                        <div className="mt-1 space-y-2">
                          <div className="relative rounded-md shadow-sm">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              aria-label={`ค่ามัดจำ รายการที่ ${index + 1}`}
                              value={item.deposit_amount}
                              // OBS-S37-001 FIX (S85): select-all on focus (เหมือนช่องค่าเช่า)
                              onFocus={(e) => e.target.select()}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0 || e.target.value === '') {
                                  updateRentalItem(index, 'deposit_amount', value);
                                }
                              }}
                              disabled={item.deposit_waived || loading}
                              className={cn('input', formErrors[`item_${index}_deposit`] && 'border-danger-500', (item.deposit_waived || loading) && 'bg-secondary-50 cursor-not-allowed text-gray-400')}
                              aria-invalid={Boolean(formErrors[`item_${index}_deposit`])}
                              aria-describedby={formErrors[`item_${index}_deposit`] ? `item_${index}_deposit-error` : undefined}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                              <span className="text-secondary-500 sm:text-sm">
                                {!item.deposit_waived && item.deposit_amount > 0 && depositImpact?.isOverLimit && (
                                  <span className="text-danger-600 text-xs">เกินวงเงิน</span>
                                )}
                              </span>
                            </div>
                          </div>
                          
                          {/* Checkbox ยกเว้นค่ามัดจำ */}
                          <div className="flex items-center mt-2">
                            <input
                              type="checkbox"
                              id={`deposit_waived_${index}`}
                              checked={item.deposit_waived || false}
                              disabled={loading}
                              onChange={(e) => {
                                const isChecked = e.target.checked;

                                setFormData(prev => {
                                  const newItems = [...prev.rental_items];
                                  newItems[index] = {
                                    ...newItems[index],
                                    deposit_waived: isChecked,
                                    ...(isChecked
                                      ? {
                                          _prev_deposit_amount: newItems[index].deposit_amount,
                                          deposit_amount: 0
                                        }
                                      : {
                                          deposit_amount: newItems[index]._prev_deposit_amount || 0
                                        })
                                  };
                                  return { ...prev, rental_items: newItems };
                                });
                              }}
                              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded cursor-pointer"
                            />
                            <label 
                              htmlFor={`deposit_waived_${index}`} 
                              className="ml-2 block text-sm text-secondary-700 cursor-pointer select-none"
                            >
                              ได้รับสิทธิ์ฟรีค่ามัดจำ <span className="text-secondary-500">(เนื่องจากค่ามัดจำเกินที่กำหนดไว้)</span>
                            </label>
                          </div>
                        </div>
                        {formErrors[`item_${index}_deposit`] && (
                          <p id={`item_${index}_deposit-error`} className="mt-1 text-sm text-danger-600">{formErrors[`item_${index}_deposit`]}</p>
                        )}
                      </div>

                      {/* มูลค่าต่อเดือน (run-rate) — ระยะเวลาจริงคิดจากวันที่เช่า (proration) ไม่ใช่ที่ระดับรายการ */}
                      <div className="sm:col-span-1 lg:col-span-2">
                        <label className="block text-sm font-medium text-secondary-700">
                          มูลค่าต่อเดือน (บาท)
                        </label>
                        <input
                          type="text"
                          aria-label={`มูลค่าต่อเดือน รายการที่ ${index + 1}`}
                          value={((item.rental_rate_per_month || 0) * (item.quantity || 1)).toLocaleString()}
                          readOnly
                          className={cn('input mt-1 bg-secondary-50 text-secondary-900 font-medium', loading && 'cursor-not-allowed')}
                        />
                        <p className="mt-1 text-sm text-secondary-500">คำนวณจาก: ค่าเช่าต่อเดือน × จำนวน</p>
                      </div>

                      {/* หมายเหตุ */}
                      <div className="sm:col-span-2 lg:col-span-6">
                        <label className="block text-sm font-medium text-secondary-700">
                          หมายเหตุ
                        </label>
                        <input
                          type="text"
                          aria-label={`หมายเหตุ รายการที่ ${index + 1}`}
                          value={item.notes}
                          onChange={(e) => updateRentalItem(index, 'notes', e.target.value)}
                          className={cn('input mt-1', formErrors[`item_${index}_notes`] && 'border-danger-500', loading && 'bg-secondary-50 cursor-not-allowed')}
                          placeholder="หมายเหตุสำหรับรายการนี้..."
                        />
                      </div>

                      {/* Remote Access (AnyDesk) */}
                      <div className="sm:col-span-2 lg:col-span-6 mt-2 pt-3 border-t border-secondary-200">
                        <div className="flex items-center gap-1.5 mb-2 text-sm font-semibold text-secondary-700">
                          <RiRemoteControlLine className="w-4 h-4 text-primary-600" />
                          Remote Access (AnyDesk)
                        </div>
                        {/* PO 2026-09-07: ระบบไม่รับ "รหัสผ่าน AnyDesk" อีกต่อไป (ใช้จริงบน prod แค่ 2 จาก 818 รายการ
                            + เป็นความลับที่ไม่ควรเก็บ plain text) — เหลือเฉพาะ ID กับหมายเหตุ */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">
                              AnyDesk ID
                            </label>
                            <input
                              type="text"
                              aria-label={`AnyDesk ID รายการที่ ${index + 1}`}
                              value={item.anydesk_id}
                              onChange={(e) => updateRentalItem(index, 'anydesk_id', e.target.value)}
                              className={cn('input mt-1 font-mono', loading && 'bg-secondary-50 cursor-not-allowed')}
                              placeholder="เช่น 123 456 789"
                              disabled={loading}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-secondary-700">
                              หมายเหตุ Remote Access
                            </label>
                            <input
                              type="text"
                              aria-label={`หมายเหตุ Remote Access รายการที่ ${index + 1}`}
                              value={item.remote_access_notes}
                              onChange={(e) => updateRentalItem(index, 'remote_access_notes', e.target.value)}
                              className={cn('input mt-1', loading && 'bg-secondary-50 cursor-not-allowed')}
                              placeholder="เช่น เครื่องอยู่หน้าเคาน์เตอร์"
                              disabled={loading}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* สรุปยอดรวม */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center gap-2 pb-2 border-b border-secondary-200 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-info-500 to-info-600 rounded-lg flex items-center justify-center shadow-sm">
                  <RiCalculatorLine className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-semibold text-secondary-800">สรุปยอดรวม</h2>
              </div>
              
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-1 lg:col-span-2">
                  <label className="block text-sm font-medium text-secondary-700">
                    ค่าเช่ารวม (ต่อเดือน)
                  </label>
                  <div className="mt-1 text-2xl font-bold text-primary-600">
                    {monthlyRentalValue.toLocaleString()} บาท/เดือน
                  </div>
                  <div className="text-xs text-secondary-500 mt-1">
                    รวมค่าเช่าทุกอุปกรณ์ต่อเดือน · ยอดเรียกเก็บจริงคิดตามจริงตอนออกใบแจ้งหนี้
                  </div>
                </div>
              </div>

              {/* แสดงข้อมูลเพิ่มเติม */}
              <div className="mt-6">
                <div className="text-sm text-secondary-500">
                    {/* แสดงจำนวนรายการที่ยกเว้นค่ามัดจำ */}
                    {(() => {
                      const waivedCount = countWaivedItems(formData.rental_items);
                      // ใช้ _prev_deposit_amount (ยอดก่อนยกเว้น) — deposit_amount ถูกบังคับเป็น 0
                      // ตอนติ๊กยกเว้น (บรรทัด ~1518) จึงสะท้อน "ยอดที่ประหยัด" ไม่ได้ถ้าอ่านจาก deposit_amount
                      const totalWaivedAmount = computeWaivedSavings(formData.rental_items);
                      if (waivedCount > 0) {
                        return (
                          <p className="text-success-600 font-medium">
                            รายการที่ได้รับสิทธิ์ฟรีค่ามัดจำ: {waivedCount} รายการ 
                            (ประหยัด {totalWaivedAmount.toLocaleString()} บาท)
                          </p>
                        );
                      }
                      return null;
                    })()}
                </div>
              </div>

              {/* แผง "ผลกระทบต่อวงเงินมัดจำ" แบบ verbose (วงเงินรวม/การใช้งาน%/คงเหลือ) ถูกตัดออก
                  ตามที่ PO ขอ — สรุปเหลือแค่ "สูงสุด / ใช้ไปแล้ว" ในแผงเด่นด้านบนพอ.
                  PO (advisory): ไม่มี validation บล็อกวงเงิน — เกินได้ ผู้สร้างตัดสินใจเอง */}
            </div>
          </div>

          {/* ปุ่มบันทึก */}
          <div className="flex flex-col gap-2">
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                type="button"
                onClick={handleBack}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-[38px] bg-white py-2 px-4 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                ยกเลิก
              </button>
              {/* PO (วงเงินมัดจำ advisory): ไม่ disable ปุ่มเมื่อเกินวงเงิน — เกินได้ ผู้สร้างตัดสินใจเอง */}
              {/* ปุ่มหลัก: เก็บเป็นร่าง (DRAFT) — ปลอดภัย ยังไม่จองอุปกรณ์ */}
              <button
                type="button"
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent, 'DRAFT')}
                disabled={loading}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-[36px] py-2 px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center bg-primary-600 hover:bg-primary-700 text-white focus:ring-primary-500"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <RiSaveLine className="h-4 w-4 mr-2" />
                    เก็บเป็นร่าง
                  </>
                )}
              </button>
              {/* ปุ่มรอง: บันทึก + เริ่มเช่าเลย (ACTIVE) — จองอุปกรณ์ทันที · ต้องมี rentals.approve (BUG-S2005-002) */}
              {canStartNow && (
              <button
                type="button"
                onClick={(e) => handleSubmit(e as unknown as React.FormEvent, 'ACTIVE')}
                disabled={loading}
                className="w-full sm:w-auto min-h-[44px] sm:min-h-[36px] py-2 px-4 rounded-md shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center border-2 border-green-600 bg-green-600 hover:bg-green-700 hover:border-green-700 text-white focus:ring-green-500"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <RiCheckLine className="h-4 w-4 mr-2" />
                    บันทึก + เริ่มเช่าเลย
                  </>
                )}
              </button>
              )}
            </div>
            {/* คำอธิบายปุ่ม */}
            <p className="text-xs text-secondary-500 text-right">
              {canStartNow
                ? 'ร่าง = ยังไม่จองอุปกรณ์ · เริ่มเช่าเลย = จองอุปกรณ์ทันที (เครื่องต้องว่าง)'
                : 'ร่าง = ยังไม่จองอุปกรณ์ · การเริ่มเช่าต้องให้ผู้มีสิทธิ์อนุมัติ (rentals.approve) กดอนุมัติร่างที่หน้ารายการเช่า'}
            </p>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default RentalCreatePageV2;